import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LoadingButton } from '@mui/lab';
import { PieChart } from '@mui/x-charts/PieChart';
import moment from 'moment';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import PermissionRequired from '../../../errorPages/permissionRequired';
import useSettings from '../../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { useSelector } from '../../../../redux/store';
import { get } from '../../../../inteceptor';
import { accessVerify, utcMoment } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';

const CLOSED_ORDER_STATUSES = ['Refunded', 'Canceled'];

const formatDateTime = (value) => (value ? utcMoment(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const getStatusColor = (status) => {
  if (status === 'closed') return 'success';
  if (status === 'open') return 'warning';
  return 'default';
};

const getOrderPaymentTotal = (payments = []) => payments.reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);

const getDrawerPaymentSummary = (payments = [], orders = [], openingAmount = 0) => {
  const orderStatusById = new Map(orders.map((order) => [Number(order.id), order.status]));
  const grouped = new Map();

  const addPayment = (payment) => {
    const paymentTypeId = payment?.paymentTypesId;
    if (paymentTypeId === null || paymentTypeId === undefined) return;
    if (!grouped.has(paymentTypeId)) {
      grouped.set(paymentTypeId, {
        id: paymentTypeId,
        name: payment?.paymentTypeName || (Number(paymentTypeId) === 1 ? 'Cash' : 'Unknown'),
        totalAmount: 0,
        transactionCount: 0,
        details: [],
      });
    }

    const group = grouped.get(paymentTypeId);
    group.totalAmount += Number(payment?.amount || 0);
    group.transactionCount += 1;
    group.details.push(payment);
  };

  payments.forEach((payment) => {
    if (CLOSED_ORDER_STATUSES.includes(orderStatusById.get(Number(payment?.posId)))) return;
    addPayment(payment);
  });

  if (Number(openingAmount || 0) > 0) {
    addPayment({
      id: 'opening-amount',
      paymentTypesId: 1,
      paymentTypeName: 'Cash',
      amount: Number(openingAmount),
      posId: 'Opening Amount',
      orderStatus: 'Opening',
      isOpeningAmount: true,
    });
  }

  return [...grouped.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

function PaymentBreakdown({ paymentSummary }) {
  const chartData = paymentSummary.map((paymentType) => ({ id: paymentType.id, value: paymentType.totalAmount, label: paymentType.name }));

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Card sx={{ p: 3, height: '100%', minHeight: 360 }}>
          <Typography variant='h6'>Payment Distribution</Typography>
          {chartData.length > 0 ? (
            <PieChart
              series={[
                {
                  data: chartData,
                  innerRadius: 55,
                  outerRadius: 110,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 45, additionalRadius: -12 },
                },
              ]}
              height={300}
            />
          ) : (
            <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                No payment data is available for this drawer.
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={7}>
        <Card sx={{ p: 3, height: '100%', maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Payment Details
          </Typography>
          {paymentSummary.length > 0 ? (
            paymentSummary.map((paymentType) => (
              <Box key={paymentType.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.100', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant='subtitle1'>{paymentType.name}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {paymentType.transactionCount} entr{paymentType.transactionCount === 1 ? 'y' : 'ies'}
                      {paymentType.details.some((detail) => detail.isOpeningAmount) ? ' - includes opening amount' : ''}
                    </Typography>
                  </Box>
                  <Typography variant='subtitle1' sx={{ whiteSpace: 'nowrap', color: 'primary.main' }}>
                    {fCurrency(paymentType.totalAmount)}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Verifier ID</TableCell>
                        <TableCell align='right'>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentType.details.map((detail, index) => (
                        <TableRow key={`${paymentType.id}-${detail?.id || index}`} hover>
                          <TableCell>{detail?.posId || '-'}</TableCell>
                          <TableCell>{detail?.orderStatus || '-'}</TableCell>
                          <TableCell>{detail?.verifierId || '-'}</TableCell>
                          <TableCell align='right'>{fCurrency(detail?.amount || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          ) : (
            <Typography variant='body2' color='text.secondary'>
              No payment summary is available.
            </Typography>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

function SessionsInDrawer({ sessions = [] }) {
  return (
    <Card sx={{ p: 3 }}>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Sessions in This Drawer
      </Typography>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Session ID</TableCell>
              <TableCell>Sequence</TableCell>
              <TableCell>Cashier</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Ended</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Orders</TableCell>
              <TableCell align='right'>Sales</TableCell>
              <TableCell align='right'>Payments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.id} hover>
                <TableCell>{session.id}</TableCell>
                <TableCell>{session.sequenceNo}</TableCell>
                <TableCell>{session.cashier?.name || '-'}</TableCell>
                <TableCell>{formatDateTime(session.startedAt)}</TableCell>
                <TableCell>{formatDateTime(session.endedAt)}</TableCell>
                <TableCell>
                  <Chip label={session.status || '-'} size='small' />
                </TableCell>
                <TableCell align='right'>{session.orderCount || 0}</TableCell>
                <TableCell align='right'>{fCurrency(session.salesAmount || 0)}</TableCell>
                <TableCell align='right'>{fCurrency(session.paymentAmount || 0)}</TableCell>
              </TableRow>
            ))}
            {sessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant='body2' color='text.secondary'>
                    No cashier sessions were recorded for this drawer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function DrawerSummary({ drawerData }) {
  const drawer = drawerData?.drawer;
  const orders = drawerData?.orders || [];
  const payments = drawerData?.payments || [];
  const totals = drawerData?.totals || {};
  const paymentSummary = useMemo(() => getDrawerPaymentSummary(payments, orders, drawer?.startingAmount), [drawer?.startingAmount, orders, payments]);

  if (!drawer)
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant='subtitle1'>Select a physical drawer to view the reconciliation report.</Typography>
      </Card>
    );

  const reconciledOrders = orders.filter((order) => !CLOSED_ORDER_STATUSES.includes(order.status));

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' spacing={2}>
            <Box>
              <Typography variant='h5'>Drawer #{drawer.id}</Typography>
              <Typography variant='body2' color='text.secondary'>
                {drawer.locationName || '-'} - Opened {formatDateTime(drawer.openedAt)} - Closed {formatDateTime(drawer.closedAt)}
              </Typography>
            </Box>
            <Chip label={drawer.status || '-'} color={getStatusColor(drawer.status)} sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }} />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Opened By
              </Typography>
              <Typography variant='subtitle2'>{drawer.openedBy?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Closed By
              </Typography>
              <Typography variant='subtitle2'>{drawer.closedBy?.name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Opening Amount
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(drawer.startingAmount || 0)}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant='overline' color='text.secondary'>
                Closing Amount
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(drawer.closingAmount || 0)}</Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 3 }}>
          <Typography variant='overline' color='text.secondary'>
            Reconciled Total
          </Typography>
          <Typography variant='h3' sx={{ mt: 1 }}>
            {fCurrency(totals.reconciledTotal || 0)}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Includes the opening amount and excludes refunded or canceled orders.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                Order Sales
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(totals.salesAmount || 0)}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                Payment Total
              </Typography>
              <Typography variant='subtitle2'>{fCurrency(totals.paymentAmount || 0)}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                Reconciled Orders
              </Typography>
              <Typography variant='subtitle2'>{totals.includedOrderCount || reconciledOrders.length}</Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant='body2' color='text.secondary'>
                All Orders
              </Typography>
              <Typography variant='subtitle2'>{totals.orderCount || orders.length}</Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <SessionsInDrawer sessions={drawerData.sessions || []} />
      </Grid>
      <Grid item xs={12}>
        <PaymentBreakdown paymentSummary={paymentSummary} />
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Orders In Drawer
          </Typography>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Order No</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Order Total</TableCell>
                  <TableCell align='right'>Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{formatDateTime(order.dateTime)}</TableCell>
                    <TableCell>{order.status || '-'}</TableCell>
                    <TableCell align='right'>{fCurrency(order.totalAmount || 0)}</TableCell>
                    <TableCell align='right'>{fCurrency(getOrderPaymentTotal(order.posPayments))}</TableCell>
                  </TableRow>
                ))}
                {orders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant='body2' color='text.secondary'>
                        No orders found for this drawer.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
    </Grid>
  );
}

export default function POSDrawerReconciliationReport() {
  const { themeStretch } = useSettings();
  const { selectedLocation } = useSelector((state) => state.location);
  const [drawerList, setDrawerList] = useState([]);
  const [selectedDrawerData, setSelectedDrawerData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [fromDate, setFromDate] = useState(moment().startOf('month'));
  const [toDate, setToDate] = useState(moment().endOf('day'));
  const [isLoading, setIsLoading] = useState(false);

  const fetchDrawers = async () => {
    if (!selectedLocation?.id || !fromDate || !toDate) return;
    setIsLoading(true);
    try {
      const response = await get('posDrawerReconciliation', {
        locationId: selectedLocation.id,
        fromDate: moment(fromDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        toDate: moment(toDate).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        status: selectedStatus,
      });
      if (response?.status) setDrawerList(response.data || []);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDrawerDetails = async (drawer) => {
    if (!drawer?.drawerId) return;
    const response = await get(`posDrawerReconciliation/${drawer.drawerId}`);
    if (response?.status) setSelectedDrawerData(response.data);
  };

  useEffect(() => {
    fetchDrawers();
  }, [selectedLocation, selectedStatus]);

  const columns = [
    { accessorKey: 'drawerId', header: 'Drawer ID' },
    { accessorKey: 'openedAt', header: 'Opened', Cell: ({ row }) => formatDateTime(row.original.openedAt) },
    { accessorKey: 'closedAt', header: 'Closed', Cell: ({ row }) => formatDateTime(row.original.closedAt) },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'openedByUserName', header: 'Opened By' },
    { accessorKey: 'closedByUserName', header: 'Closed By' },
    { accessorKey: 'startingAmount', header: 'Opening Amount', Cell: ({ row }) => fCurrency(row.original.startingAmount || 0) },
    { accessorKey: 'closingAmount', header: 'Closing Amount', Cell: ({ row }) => fCurrency(row.original.closingAmount || 0) },
    { accessorKey: 'salesAmount', header: 'Sales', Cell: ({ row }) => fCurrency(row.original.salesAmount || 0) },
    { accessorKey: 'paymentAmount', header: 'Payments', Cell: ({ row }) => fCurrency(row.original.paymentAmount || 0) },
    { accessorKey: 'status', header: 'Status', Cell: ({ row }) => <Chip label={row.original.status || '-'} color={getStatusColor(row.original.status)} size='small' /> },
  ];

  return (
    <Page title='POS Drawer Reconciliation'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs heading='POS Drawer Reconciliation' links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Reports', href: '' }, { name: 'POS Drawer Reconciliation' }]} />
        {accessVerify('POS_SESSION_RECONCILIATION_REPORT_VIEW') ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={3}>
                    <DatePicker label='From Date' value={fromDate} onChange={(newValue) => newValue && setFromDate(moment(newValue))} renderInput={(params) => <TextField {...params} fullWidth />} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker label='To Date' value={toDate} onChange={(newValue) => newValue && setToDate(moment(newValue))} renderInput={(params) => <TextField {...params} fullWidth />} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id='drawer-status-label'>Status</InputLabel>
                      <Select labelId='drawer-status-label' value={selectedStatus} label='Status' onChange={(event) => setSelectedStatus(event.target.value)}>
                        <MenuItem value='all'>All</MenuItem>
                        <MenuItem value='open'>Open</MenuItem>
                        <MenuItem value='closed'>Closed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LoadingButton fullWidth variant='contained' size='large' loading={isLoading} onClick={fetchDrawers}>
                      Load Drawers
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <DataGridTable
                name='POS Drawer Reconciliation Drawers'
                data={drawerList}
                column={columns}
                onRowClick={(row) => fetchDrawerDetails(row?.original)}
                isRowClickable
                rowSelection={false}
                setRowSelection={() => {}}
                enableRowSelection={false}
                enableRowActions={false}
                renderRowActionItems={() => []}
              />
            </Grid>
            <Grid item xs={12}>
              <DrawerSummary drawerData={selectedDrawerData} />
            </Grid>
          </Grid>
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
}
